import * as React from "react";
import {Display} from "../../components/Display";
import {UsersAccessesByDay} from "./components/UsersAccessesByDay";
import {ActiveUsers} from "./components/ActiveUsers";
import {DATA_STATE, useUserDashboardData} from "../contexts/UsersDashboardContext";
import {uuid} from "../../types/veboxModels";
import {UsersAccessesByHierarchy} from "./components/UsersAccessesByHierarchy";
import {Loader} from "../../components/Utils/loader/Loader";
import {UserListContext} from "../contexts/UserListContext";
import {UsersAccessesInTheCompany} from "./components/UsersAccessesInTheCompany";

enum chartType {
    CHART_PIE_ACTIVE_USERS = "730cd478-5be7-477d-abb1-8ec0350bc33b",
    BAR_CHART_HIERARCHY_USER_RECENTLY_CONNECTED = "72299680-76ed-4ce2-b28a-ee3adc25ecd7",
    BAR_CHART_USER_RECENTLY_CONNECTED_BY_DAY = "d72f2c56-95f5-4951-87f5-9349b7ec71e0",
    PIE_CHART_USER_RECENTLY_CONNECTED = "c98aee8b-b291-4233-8b23-94eecf70e85c"
}

type UsersWidget = {
    size: "small" | "large",
    widget: JSX.Element
}
const getWidgetByGuid = (guid: uuid): UsersWidget => {
    switch (guid) {
        case chartType.BAR_CHART_HIERARCHY_USER_RECENTLY_CONNECTED: {
            return {
                size: "small",
                widget: <UsersAccessesByHierarchy/>
            }
        }
        case chartType.CHART_PIE_ACTIVE_USERS: {
            return {
                size: "small",
                widget: <ActiveUsers/>
            }
        }
        case chartType.BAR_CHART_USER_RECENTLY_CONNECTED_BY_DAY: {
            return {
                size: "large",
                widget: <UsersAccessesByDay/>
            }
        }
        case chartType.PIE_CHART_USER_RECENTLY_CONNECTED: {
            return {
                size: "small",
                widget: <UsersAccessesInTheCompany/>
            }
        }
        default: {
            return {
                size: "large",
                widget: <></>
            }
        }
    }
}

const getFirstSmallWidget = (widgetsList: UsersWidget[]): { list: UsersWidget[], smallWidget: UsersWidget } => {
    const list: UsersWidget[] = [];
    let smallWidget: UsersWidget;
    widgetsList.forEach((wd) => {
        if (!smallWidget && wd.size === "small") {
            smallWidget = wd
        } else {
            list.push(wd)
        }
    })
    return {smallWidget, list}
}
/**
 * Covers holes in the dashboard grid with the first small widget in give list
 */
const prepareWidgetsList = (widgetsList: UsersWidget[]): UsersWidget[] => {
    if (widgetsList.length > 0) {
        const tmpList: UsersWidget[] = []
        let chartsNumber = 0;
        for (let i = 0; i < widgetsList.length; i++) {
            const wd = widgetsList[i];
            tmpList.push(wd)
            chartsNumber = wd.size === "small" ? chartsNumber + 1 : chartsNumber + 2
            if (chartsNumber === 1) {
                if (i + 1 === widgetsList.length) {
                    tmpList.push({size: "small", widget: <></>}) // if you want to have a hole
                    // if you want to have a large widget
                    // tmpList.pop();
                    // tmpList.push({size: "large", widget: wd.widget})
                } else if (widgetsList[i + 1].size === "large") {
                    const tmp = getFirstSmallWidget(widgetsList.slice(i + 1))
                    if (tmp.smallWidget) {
                        tmpList.push(tmp.smallWidget)
                        return [...tmpList, ...prepareWidgetsList(tmp.list)]
                    } else {
                        tmpList.push({size: "small", widget: <></>}) // if you want to have a hole
                        // if you want to have a large widget
                        // tmpList.pop();
                        // tmpList.push({size: "large", widget: wd.widget})
                    }
                }
            } else if (chartsNumber === 2) {
                chartsNumber = 0
            }
        }
        return tmpList
    }
    return []
}

const getWidgetsList = (widgetsList: uuid[]): UsersWidget[] => {
    const tmpList: UsersWidget[] = widgetsList
        .map((widgetGuid) => {
            const elem = getWidgetByGuid(widgetGuid);
            return {
                size: elem.size,
                widget: elem.widget
            }
        })
    return prepareWidgetsList(tmpList)
}

const createUsersDashboard = (widgetsList: uuid[]): JSX.Element [] => {
    const chartsList = getWidgetsList(widgetsList);
    const rowsList: JSX.Element[] = [];
    let row: JSX.Element[] = []
    chartsList.forEach((widget, index) => {
        if (widget.size === "small") {
            row.push(
                <div className={"Section"} key={"section_" + index}>
                    {widget.widget}
                </div>
            )
            if (row.length === 2) {
                rowsList.push(<div className={"row"} key={"row_" + rowsList.length + 1}>{row}</div>);
                row = []
            }
        } else {
            rowsList.push(
                <div className={"row"} key={"row_" + rowsList.length + 1}>
                    <div className={"Section"} key={"section_" + index}>
                        {widget.widget}
                    </div>
                </div>
            );
        }
    })
    return rowsList
}

export const UsersDashboard: React.FC<any> = () => {
    const [wait, setWait] = React.useState<boolean>(true)
    const {widgetsList, state} = useUserDashboardData().widgetsDashboardState;
    const {loadDashboardData, isFistRender} = useUserDashboardData();
    const [usersDashboardWidgetsList, setUsersDashboardWidgetsList] = React.useState<JSX.Element []>([]);
    const {filtersUpdated} = React.useContext(UserListContext);

    React.useEffect(() => {
        if (state === DATA_STATE.loaded) {
            setUsersDashboardWidgetsList(createUsersDashboard(widgetsList))
            setTimeout(() => setWait(false), 200)
        }
    }, [state])

    React.useEffect(() => {
        if (!isFistRender && !filtersUpdated) {
            loadDashboardData();
        }
    }, [])

    return (
        <div className={"user-management"}>

            <div className={"wrapper"}>
                <div>
                    <Display isVisible={state === DATA_STATE.loaded && !wait}>
                        {usersDashboardWidgetsList}
                    </Display>
                    <Display isVisible={state === DATA_STATE.isLoading || wait}>
                        <Loader/>
                    </Display>
                </div>
            </div>
        </div>
    )
}